import { useEffect, useMemo, useRef, useState } from 'react';

type UseInterviewClockOptions = {
  interviewId?: string;
  durationSeconds?: number;
  initialTimestamp?: string;
};

export function useInterviewClock(options: UseInterviewClockOptions) {
  const { interviewId, durationSeconds = 0, initialTimestamp } = options;
  const [totalSeconds, setTotalSeconds] = useState<number>(0);
  const startRef = useRef<number | null>(null);

  const parseTimestamp = (timestamp?: string): number => {
    if (!timestamp) return 0;
    const trimmed = timestamp.trim();
    if (!trimmed) return 0;
    const normalized = trimmed.includes(':')
      ? trimmed
      : `${trimmed.slice(0, 2)}:${trimmed.slice(2, 4)}:${trimmed.slice(4, 6)}`;
    const parts = normalized.split(':').map((n) => Number.parseInt(n, 10));
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return 0;
    const [hh = 0, mm = 0, ss = 0] = parts;
    return hh * 3600 + mm * 60 + ss;
  };

  useEffect(() => {
    if (!interviewId) return;
    const parsedSeconds = parseTimestamp(initialTimestamp);
    startRef.current = Date.now() - parsedSeconds * 1000;
    setTotalSeconds(parsedSeconds);

    const interval = setInterval(() => {
      const startedAt = startRef.current;
      if (!startedAt) return;
      const diff = Math.floor((Date.now() - startedAt) / 1000);
      setTotalSeconds(diff);
    }, 1000);

    return () => clearInterval(interval);
  }, [interviewId, initialTimestamp]);

  const timeLabel = useMemo(() => {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const hh = hrs.toString().padStart(2, '0');
    const mm = mins.toString().padStart(2, '0');
    const ss = secs.toString().padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }, [totalSeconds]);

  // Keep timestamp in HH:mm:ss format for backend
  const timestampHHMMSS = useMemo(() => timeLabel, [timeLabel]);

  const progressPercentage = useMemo(() => {
    if (!durationSeconds || durationSeconds <= 0) return 0;
    return Math.min(100, (totalSeconds / durationSeconds) * 100);
  }, [durationSeconds, totalSeconds]);

  return {
    timeLabel,
    totalSeconds,
    progressPercentage,
    timestampHHMMSS,
  };
}
